<template>
  <div class="modal fade" id="taxpayerDetailsModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-account-details me-2"></i>
            Taxpayer Details - {{ taxpayer.name }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <div class="row">
            <!-- Left Column - Basic Info -->
            <div class="col-md-6">
              <div class="card border">
                <div class="card-header bg-primary text-white">
                  <h5 class="mb-0 text-white">
                    <i class="mdi mdi-account me-2"></i>
                    Basic Information
                  </h5>
                </div>
                <div class="card-body">
                  <div class="d-flex align-items-center mb-4">
                    <div class="avatar-lg rounded-circle bg-primary me-3">
                      <span class="avatar-title rounded-circle bg-primary text-white fs-2">
                        {{ taxpayer.name ? taxpayer.name.charAt(0).toUpperCase() : 'T' }}
                      </span>
                    </div>
                    <div>
                      <h5 class="mb-1">{{ taxpayer.name }}</h5>
                      <p v-if="taxpayer.businessName" class="text-muted mb-0">{{ taxpayer.businessName }}</p>
                      <span class="badge" :class="getTaxpayerTypeClass(taxpayer.type)">
                        {{ taxpayer.type }}
                      </span>
                    </div>
                  </div>

                  <table class="table table-borderless table-sm">
                    <tr>
                      <td class="fw-bold">National ID:</td>
                      <td>{{ taxpayer.nationalId }}</td>
                    </tr>
                    <tr v-if="taxpayer.businessRegNo">
                      <td class="fw-bold">Business Reg No:</td>
                      <td>{{ taxpayer.businessRegNo }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">KRA PIN:</td>
                      <td class="fw-bold text-primary">{{ taxpayer.kraPin }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Phone:</td>
                      <td>{{ taxpayer.phone }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Email:</td>
                      <td>{{ taxpayer.email }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Registration Date:</td>
                      <td>{{ taxpayer.registrationDate }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Status:</td>
                      <td>
                        <span class="badge" :class="getStatusClass(taxpayer.status)">
                          {{ taxpayer.status }}
                        </span>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>

              <!-- Locations Card -->
              <div class="card border mt-3">
                <div class="card-header bg-info text-white">
                  <h5 class="mb-0 text-white">
                    <i class="mdi mdi-map-marker me-2"></i>
                    Operating Locations
                  </h5>
                </div>
                <div class="card-body">
                  <div v-if="taxpayer.locations && taxpayer.locations.length > 0">
                    <div v-for="location in taxpayer.locations" :key="location" class="mb-2">
                      <div class="d-flex align-items-center">
                        <i class="mdi mdi-map-marker text-info me-2"></i>
                        <span>{{ location }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-muted text-center py-3">
                    No locations recorded
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column - Revenue Info -->
            <div class="col-md-6">
              <div class="card border">
                <div class="card-header bg-success text-white">
                  <h5 class="mb-0 text-white">
                    <i class="mdi mdi-currency-usd me-2"></i>
                    Revenue Information
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row mb-3">
                    <div class="col-12">
                      <div class="bg-light rounded p-3 text-center">
                        <h3 class="mb-1">{{ formatPrice(taxpayer.monthlyRevenue) }}</h3>
                        <small class="text-muted">Monthly Revenue</small>
                      </div>
                    </div>
                  </div>

                  <h6 class="mb-3">Revenue Streams</h6>
                  <div v-if="taxpayer.revenueStreams && taxpayer.revenueStreams.length > 0">
                    <div v-for="stream in taxpayer.revenueStreams" :key="stream.type + stream.location"
                         class="border rounded p-3 mb-3">
                      <div class="d-flex justify-content-between align-items-start">
                        <div>
                          <span class="badge bg-primary mb-1">{{ stream.type }}</span>
                          <div class="text-muted">
                            <i class="mdi mdi-map-marker me-1"></i>
                            {{ stream.location }}
                          </div>
                        </div>
                        <div class="text-end">
                          <div class="fw-bold">{{ formatPrice(stream.amount) }}</div>
                          <small class="text-muted">per month</small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-muted text-center py-3">
                    No revenue streams recorded
                  </div>
                </div>
              </div>

              <!-- Quick Stats -->
              <div class="card border mt-3">
                <div class="card-header bg-warning text-dark">
                  <h5 class="mb-0">
                    <i class="mdi mdi-chart-bar me-2"></i>
                    Quick Statistics
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row text-center">
                    <div class="col-4">
                      <div class="border-end">
                        <h4 class="mb-1">{{ taxpayer.revenueStreams ? taxpayer.revenueStreams.length : 0 }}</h4>
                        <small class="text-muted">Revenue Streams</small>
                      </div>
                    </div>
                    <div class="col-4">
                      <div class="border-end">
                        <h4 class="mb-1">{{ taxpayer.locations ? taxpayer.locations.length : 0 }}</h4>
                        <small class="text-muted">Locations</small>
                      </div>
                    </div>
                    <div class="col-4">
                      <h4 class="mb-1">{{ calculateYearsActive() }}</h4>
                      <small class="text-muted">Years Active</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Activity Section -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="card border">
                <div class="card-header bg-secondary text-white">
                  <h5 class="mb-0 text-white">
                    <i class="mdi mdi-history me-2"></i>
                    Recent Activity
                  </h5>
                </div>
                <div class="card-body">
                  <div class="timeline">
                    <div class="timeline-item">
                      <div class="timeline-marker bg-success"></div>
                      <div class="timeline-content">
                        <h6 class="mb-1">Payment Received</h6>
                        <p class="mb-1">Single Business Permit payment of KES 85,000 received</p>
                        <small class="text-muted">2 days ago</small>
                      </div>
                    </div>
                    <div class="timeline-item">
                      <div class="timeline-marker bg-info"></div>
                      <div class="timeline-content">
                        <h6 class="mb-1">Data Sync</h6>
                        <p class="mb-1">Taxpayer data synchronized with KRA database</p>
                        <small class="text-muted">1 week ago</small>
                      </div>
                    </div>
                    <div class="timeline-item">
                      <div class="timeline-marker bg-warning"></div>
                      <div class="timeline-content">
                        <h6 class="mb-1">Profile Update</h6>
                        <p class="mb-1">Contact information updated</p>
                        <small class="text-muted">2 weeks ago</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button @click="generateReport" class="btn btn-success me-2">
                <i class="mdi mdi-file-document me-1"></i>
                Generate Report
              </button>
              <button @click="syncData" class="btn btn-info">
                <i class="mdi mdi-sync me-1"></i>
                Sync Data
              </button>
            </div>
            <div>
              <button @click="editTaxpayer" class="btn btn-primary me-2">
                <i class="mdi mdi-pencil me-1"></i>
                Edit
              </button>
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from "moment";

export default {
  name: "TaxpayerDetailsModal",
  props: {
    taxpayer: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value);
    },

    getTaxpayerTypeClass(type) {
      switch (type) {
        case 'INDIVIDUAL':
          return 'bg-info';
        case 'BUSINESS':
          return 'bg-success';
        case 'ORGANIZATION':
          return 'bg-warning';
        default:
          return 'bg-secondary';
      }
    },

    getStatusClass(status) {
      switch (status) {
        case 'ACTIVE':
          return 'bg-success';
        case 'INACTIVE':
          return 'bg-secondary';
        case 'SUSPENDED':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    calculateYearsActive() {
      if (!this.taxpayer.registrationDate) return 0;
      return moment().diff(moment(this.taxpayer.registrationDate), 'years');
    },

    editTaxpayer() {
      this.$emit('edit-taxpayer', this.taxpayer);

      // Close this modal
      const modalElement = document.getElementById('taxpayerDetailsModal');
      if (modalElement) {
        const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
        if (bootstrapModal) {
          bootstrapModal.hide();
        }
      }
    },

    generateReport() {
      console.log('Generating report for:', this.taxpayer.name);
      alert(`Generating comprehensive report for ${this.taxpayer.name}...`);
    },

    syncData() {
      console.log('Syncing data for:', this.taxpayer.name);
      alert(`Syncing data for ${this.taxpayer.name} with external databases...`);
    }
  }
}
</script>

<style scoped>
.avatar-lg {
  width: 4rem;
  height: 4rem;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.table-borderless td {
  border: none;
  padding: 0.5rem 0;
}

.timeline {
  position: relative;
  padding-left: 2rem;
}

.timeline-item {
  position: relative;
  margin-bottom: 1.5rem;
}

.timeline-item:not(:last-child):before {
  content: '';
  position: absolute;
  left: -1.5rem;
  top: 1.5rem;
  height: calc(100% + 1rem);
  width: 2px;
  background-color: #dee2e6;
}

.timeline-marker {
  position: absolute;
  left: -1.75rem;
  top: 0.25rem;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  border: 2px solid white;
}

.timeline-content {
  margin-left: 0.5rem;
}

.border-end {
  border-right: 1px solid #dee2e6 !important;
}

.modal-xl {
  max-width: 1200px;
}

.card-header.bg-primary h5,
.card-header.bg-success h5,
.card-header.bg-info h5,
.card-header.bg-secondary h5 {
  color: white !important;
}
</style>